import { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { CheckCircle, Flame, Trophy, Star, Zap } from 'lucide-react';

export type CelebrationType =
  | 'lesson_complete'
  | 'streak_milestone'
  | 'quiz_passed'
  | 'level_up'
  | 'achievement'
  | 'first_lesson_today';

interface CelebrationConfig {
  icon: React.ReactNode;
  color: string;
  confettiColors: string[];
  duration: number;
}

const celebrationConfigs: Record<CelebrationType, CelebrationConfig> = {
  lesson_complete: {
    icon: <CheckCircle className="w-12 h-12" />,
    color: 'text-green-500',
    confettiColors: ['#22c55e', '#16a34a', '#4ade80', '#86efac'],
    duration: 3000,
  },
  streak_milestone: {
    icon: <Flame className="w-12 h-12" />,
    color: 'text-orange-500',
    confettiColors: ['#f97316', '#ea580c', '#fb923c', '#fdba74'],
    duration: 4000,
  },
  quiz_passed: {
    icon: <Trophy className="w-12 h-12" />,
    color: 'text-yellow-500',
    confettiColors: ['#eab308', '#ca8a04', '#facc15', '#fde047'],
    duration: 3500,
  },
  level_up: {
    icon: <Star className="w-12 h-12" />,
    color: 'text-purple-500',
    confettiColors: ['#a855f7', '#9333ea', '#c084fc', '#d8b4fe'],
    duration: 4000,
  },
  achievement: {
    icon: <Trophy className="w-12 h-12" />,
    color: 'text-blue-500',
    confettiColors: ['#3b82f6', '#2563eb', '#60a5fa', '#93c5fd'],
    duration: 3500,
  },
  first_lesson_today: {
    icon: <Zap className="w-12 h-12" />,
    color: 'text-amber-500',
    confettiColors: ['#f59e0b', '#d97706', '#fbbf24', '#fcd34d'],
    duration: 3000,
  },
};

export interface CelebrationData {
  type: CelebrationType;
  message: string;
  subMessage?: string;
  xpEarned?: number;
}

interface CelebrationOverlayProps {
  celebration: CelebrationData | null;
  onComplete: () => void;
}

export function CelebrationOverlay({ celebration, onComplete }: CelebrationOverlayProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (celebration) {
      setIsVisible(true);
      setShowConfetti(true);

      const config = celebrationConfigs[celebration.type];

      // Hide confetti after a bit
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, config.duration - 500);

      // Hide overlay
      const hideTimer = setTimeout(() => {
        setIsVisible(false);
        onComplete();
      }, config.duration);

      return () => {
        clearTimeout(confettiTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [celebration, onComplete]);

  if (!celebration || !isVisible) return null;

  const config = celebrationConfigs[celebration.type];

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
      {/* Confetti */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={150}
          gravity={0.3}
          colors={config.confettiColors}
          tweenDuration={100}
        />
      )}

      {/* Celebration Card */}
      <div
        className={`
          pointer-events-auto
          bg-white dark:bg-gray-900
          rounded-3xl shadow-2xl
          p-8 max-w-sm mx-4
          transform transition-all duration-300
          ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}
          animate-in zoom-in-95 duration-300
        `}
      >
        <div className="text-center">
          {/* Icon */}
          <div className={`${config.color} mb-4 flex justify-center animate-bounce`}>
            {config.icon}
          </div>

          {/* Message */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {celebration.message}
          </h2>

          {celebration.subMessage && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {celebration.subMessage}
            </p>
          )}

          {/* XP Badge */}
          {celebration.xpEarned && celebration.xpEarned > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold">
              <Zap className="w-5 h-5" />
              +{celebration.xpEarned} XP
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
